import React, { useMemo } from "react";
import {
  alpha,
  Alert,
  Box,
  Chip,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import StadiumIcon from "@mui/icons-material/Stadium";
import { useAdp } from "../context/AdpContext";
import { EmptyState } from "../components/ui";
import { getTeamColor } from "../config/theme";
import { DOME_STADIUMS } from "../services/stackDetection";

// Progressive green intensities for the fantasy playoff weeks — week 17
// (championship) is the boldest. Keyed by week number.
const PLAYOFF_TINTS: Record<number, number> = { 15: 0.08, 16: 0.16, 17: 0.3 };

/** Parse a matchup string ("@LAC", "SEA", "BYE") into its parts. */
function parseMatchup(matchup: string | undefined) {
  if (!matchup) return { isBye: true, isAway: false, opp: "" };
  if (matchup === "BYE") return { isBye: true, isAway: false, opp: "" };
  const isAway = matchup.startsWith("@");
  return { isBye: false, isAway, opp: (isAway ? matchup.slice(1) : matchup).trim() };
}

export default function SchedulePage() {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const { schedule, scheduleError } = useAdp();

  const teamKeys = useMemo(() => {
    if (!schedule) return [] as string[];
    return Object.keys(schedule.weekSchedules).sort((a, b) => a.localeCompare(b));
  }, [schedule]);

  return (
    <Box sx={{ p: 2.5, pb: { xs: 10, md: 2.5 } }}>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1, mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 700, color: "text.primary" }}>Schedule</Typography>
        <Typography variant="body2" sx={{ color: "text.secondary" }}>
          The 2026 NFL schedule, one row per team. Weeks 15 to 17 are the fantasy playoffs and are
          highlighted, darkening toward the championship in week 17.
        </Typography>
      </Box>

      {scheduleError && (
        <Alert severity="info" sx={{ mb: 3 }}>
          {scheduleError}
        </Alert>
      )}

      {!schedule ? (
        <Paper variant="outlined" sx={{ overflow: "hidden" }}>
          <EmptyState
            icon={<CalendarMonthOutlinedIcon />}
            title="Schedule not available yet"
            hint="It will appear here once the remote schedule asset has been published."
          />
        </Paper>
      ) : (
        <Paper variant="outlined" sx={{ overflow: "hidden" }}>
          <Box sx={{ px: 2.5, py: 2, borderBottom: `1px solid ${theme.palette.divider}` }}>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, alignItems: "center" }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>2026 Schedule</Typography>
              <Chip label={`${teamKeys.length} teams`} size="small" />
              <Chip label={`${schedule.weekNums.length} weeks`} size="small" />
            </Box>
            {/* Legend */}
            <Stack direction="row" spacing={2.5} sx={{ mt: 1.25, flexWrap: "wrap", rowGap: 0.5 }}>
              <Stack direction="row" spacing={0.5} alignItems="center">
                <StadiumIcon sx={{ fontSize: 15, color: "text.secondary" }} />
                <Typography variant="caption" sx={{ color: "text.secondary" }}>Dome game</Typography>
              </Stack>
              <Stack direction="row" spacing={0.5} alignItems="center">
                <Box sx={{ width: 14, height: 14, borderRadius: 0.5, bgcolor: alpha(theme.palette.primary.main, PLAYOFF_TINTS[17]) }} />
                <Typography variant="caption" sx={{ color: "text.secondary" }}>Fantasy playoffs (Wk 15–17)</Typography>
              </Stack>
              <Stack direction="row" spacing={0.5} alignItems="center">
                <Typography variant="caption" sx={{ color: "text.secondary" }}>
                  <Box component="span" sx={{ color: "text.disabled" }}>@</Box> = away
                </Typography>
              </Stack>
            </Stack>
          </Box>
          <TableContainer sx={{ overflowX: "auto", maxHeight: "calc(100vh - 220px)" }}>
            <Table stickyHeader sx={{ minWidth: 1000, borderCollapse: "separate" }}>
              <TableHead>
                <TableRow>
                  <TableCell
                    sx={{
                      backgroundColor: theme.palette.background.paper,
                      position: "sticky",
                      left: 0,
                      top: 0,
                      zIndex: 3,
                      fontWeight: 700,
                      borderRight: `1px solid ${theme.palette.divider}`,
                    }}
                  >
                    Team
                  </TableCell>
                  {schedule.weekNums.map((weekNum) => {
                    const tint = PLAYOFF_TINTS[weekNum];
                    return (
                      <TableCell
                        key={weekNum}
                        align="center"
                        sx={{
                          backgroundColor: tint
                            ? alpha(theme.palette.primary.main, tint)
                            : theme.palette.background.paper,
                          top: 0,
                          zIndex: 2,
                          whiteSpace: "nowrap",
                          px: 1,
                          fontWeight: tint ? 700 : undefined,
                          borderLeft: weekNum === 15 ? `2px solid ${theme.palette.primary.main}` : undefined,
                        }}
                      >
                        Wk {weekNum}
                      </TableCell>
                    );
                  })}
                </TableRow>
              </TableHead>
              <TableBody>
                {teamKeys.map((team, rowIndex) => {
                  const teamColor = getTeamColor(team, isDark, "fg");
                  const zebra = rowIndex % 2 === 1;
                  return (
                    <TableRow key={team} hover>
                      <TableCell
                        sx={{
                          fontWeight: 700,
                          color: teamColor,
                          position: "sticky",
                          left: 0,
                          zIndex: 1,
                          // Opaque so horizontally-scrolled cells don't bleed
                          // through the frozen column.
                          backgroundColor: theme.palette.background.paper,
                          borderLeft: `3px solid ${teamColor}`,
                          borderRight: `1px solid ${theme.palette.divider}`,
                        }}
                      >
                        {team}
                      </TableCell>
                      {schedule.weekNums.map((weekNum, index) => {
                        const matchup = schedule.weekSchedules[team]?.[index];
                        const { isBye, isAway, opp } = parseMatchup(matchup);
                        const tint = PLAYOFF_TINTS[weekNum];
                        // Dome if the game is played in a dome stadium: away → at
                        // the opponent's venue; home → at this team's venue.
                        const isDome =
                          !isBye && (isAway ? DOME_STADIUMS.has(opp) : DOME_STADIUMS.has(team));
                        return (
                          <TableCell
                            key={`${team}-${weekNum}`}
                            align="center"
                            sx={{
                              whiteSpace: "nowrap",
                              px: 1,
                              py: 0.75,
                              bgcolor: tint
                                ? alpha(theme.palette.primary.main, tint)
                                : zebra
                                ? theme.palette.action.hover
                                : "inherit",
                              borderLeft:
                                weekNum === 15 ? `2px solid ${theme.palette.primary.main}` : undefined,
                            }}
                          >
                            {isBye ? (
                              <Typography
                                component="span"
                                variant="body2"
                                sx={{ color: "text.disabled", fontStyle: "italic" }}
                              >
                                BYE
                              </Typography>
                            ) : (
                              <Stack
                                direction="row"
                                spacing={0.25}
                                alignItems="center"
                                justifyContent="center"
                                component="span"
                              >
                                {isAway && (
                                  <Box component="span" sx={{ color: "text.disabled" }}>@</Box>
                                )}
                                <Box component="span">{opp}</Box>
                                {isDome && (
                                  <Tooltip title="Dome game" arrow>
                                    <StadiumIcon sx={{ fontSize: 14, color: "text.secondary", ml: 0.25 }} />
                                  </Tooltip>
                                )}
                              </Stack>
                            )}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}
    </Box>
  );
}
